import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import type { SxProps, Theme } from "@mui/material/styles";
import { useTabParam } from "./useTabParam";

export interface TabbedPageTab {
  id: string;
  label: string;
  content: ReactNode;
}

/**
 * A reusable MUI `Tabs`/panel wrapper so a sidebar destination can host several sub-views
 * instead of one long scroll (docs/365-ADMIN-USER-COMPLETION-AND-CATALOGS-TABS.md item 3/4).
 * The active tab is synced to the `?tab=` search param via {@link useTabParam} — deep-
 * linkable and refresh-stable, matching the admin's existing routed-page discipline.
 *
 * Only the active tab's `content` is mounted at a time (a plain conditional render, not
 * `keep-mounted` panels) — every current adopter (`CatalogsPage`/`ProviderSettingsPage`) owns
 * its own `useQuery`/local state per tab section already, so remounting on switch is cheap
 * and avoids background tabs holding stale subscriptions.
 */
export function TabbedPage({
  tabs,
  defaultTab,
  sx,
}: {
  tabs: TabbedPageTab[];
  /** Defaults to `tabs[0].id`. */
  defaultTab?: string;
  sx?: SxProps<Theme>;
}) {
  const tabIds = tabs.map((t) => t.id);
  const [active, setActive] = useTabParam(tabIds, defaultTab);
  const current = tabs.find((t) => t.id === active) ?? tabs[0];

  return (
    <Box sx={sx}>
      <Tabs
        value={current?.id ?? false}
        onChange={(_event, value: string) => setActive(value)}
        sx={{ mb: 2.5, borderBottom: 1, borderColor: "divider" }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        {tabs.map((t) => (
          <Tab key={t.id} value={t.id} label={t.label} />
        ))}
      </Tabs>
      {current ? <Box role="tabpanel">{current.content}</Box> : null}
    </Box>
  );
}
